import React, { useState, useEffect } from "react";
import userService from "../services/userService";
import { toast } from "react-toastify";
import { FaUserShield, FaUserCheck, FaUserTimes, FaSync, FaTrashAlt, FaEnvelopeOpenText, FaPowerOff, FaToggleOn, FaToggleOff } from "react-icons/fa";
import styles from "../style/pages/UserManagementPage.module.css";

const UserManagementPage = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [processingId, setProcessingId] = useState(null);
  const [currentUserId, setCurrentUserId] = useState(null);

  useEffect(() => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        setCurrentUserId(user.user_id || user.userId);
      } catch (e) {
        console.error("Failed to parse user from localStorage", e);
      }
    }
  }, []);

  const fetchUsers = async (showToast = false) => {
    if (showToast) setRefreshing(true);
    try {
      const data = await userService.getAllUsers();
      setUsers(data);
      if (showToast) toast.success("User list refreshed");
    } catch (error) {
      toast.error(error.message || "Failed to fetch users");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleStatusToggle = async (userId, currentStatus) => {
    setProcessingId(userId);
    try {
      await userService.toggleUserStatus(userId, !currentStatus);
      toast.success(`User ${!currentStatus ? 'activated' : 'deactivated'} successfully`);
      fetchUsers();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setProcessingId(null);
    }
  };

  const handleDeleteUser = async (userId, name) => {
    if (window.confirm(`Are you sure you want to delete user "${name}"? This action cannot be undone.`)) {
      setProcessingId(userId);
      try {
        await userService.deleteUser(userId);
        toast.success("User deleted successfully");
        fetchUsers();
      } catch (error) {
        toast.error(error.message);
      } finally {
        setProcessingId(null);
      }
    }
  };

  const handleResendVerification = async (userId) => {
    setProcessingId(userId);
    try {
      await userService.manualResendVerification(userId);
      toast.success("Verification email sent successfully");
    } catch (error) {
      toast.error(error.message);
    } finally {
      setProcessingId(null);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loader}></div>
        <p>Loading Users...</p>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerTitle}>
          <h2><FaUserShield /> User Management</h2>
          <p>Monitor and manage platform users and their verification status.</p>
        </div>
        <button 
          className={styles.refreshBtn} 
          onClick={() => fetchUsers(true)}
          disabled={refreshing}
        >
          <FaSync className={refreshing ? styles.spin : ""} /> Refresh
        </button>
      </div>

      <div className={styles.tableCard}>
        <div className={styles.tableWrapper}>
          <table className={styles.userTable}>
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Username</th>
                <th>Role</th>
                <th>Status</th>
                <th>Account</th>
                <th className={styles.actionsHeader}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.length === 0 ? (
                <tr>
                  <td colSpan="7" className={styles.noData}>No users found.</td>
                </tr>
              ) : (
                users.map((user) => (
                  <tr key={user.userId}>
                    <td className={styles.nameCell}>
                      {user.name} 
                      {user.userId === currentUserId && <span className={styles.youLabel}>(You)</span>}
                    </td>
                    <td>{user.email}</td>
                    <td className={styles.usernameCell}>{user.userName || user.email}</td>
                    <td>
                      <span className={`${styles.badge} ${user.authRole === 'admin' ? styles.adminBadge : styles.userBadge}`}>
                        {user.authRole || "N/A"}
                      </span>
                    </td>
                    <td>
                      {user.emailVerified ? (
                        <span className={styles.verified}>
                          <FaUserCheck /> Verified
                        </span>
                      ) : (
                        <span className={styles.unverified}>
                          <FaUserTimes /> Unverified
                        </span>
                      )}
                    </td>
                    <td>
                      <span className={user.enabled ? styles.activeText : styles.inactiveText}>
                        {user.enabled ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className={styles.actionsCell}>
                      <div className={styles.actionGroup}>
                        {!user.emailVerified && (
                          <button 
                            className={styles.actionBtn} 
                            onClick={() => handleResendVerification(user.userId)}
                            title="Resend Verification Email"
                            disabled={processingId === user.userId}
                          >
                            <FaEnvelopeOpenText color="#4f46e5" />
                          </button>
                        )}
                        <button 
                          className={styles.actionBtn} 
                          onClick={() => handleStatusToggle(user.userId, user.enabled)}
                          title={user.userId === currentUserId ? "You cannot deactivate your own account" : (user.enabled ? "Deactivate Account" : "Activate Account")}
                          disabled={processingId === user.userId || user.userId === currentUserId}
                        >
                          {user.enabled ? 
                            <FaToggleOn color={user.userId === currentUserId ? "#d1d5db" : "#10b981"} /> : 
                            <FaToggleOff color="#9ca3af" />
                          }
                        </button>
                        <button 
                          className={styles.actionBtn} 
                          onClick={() => handleDeleteUser(user.userId, user.name)}
                          title={user.userId === currentUserId ? "You cannot delete your own account" : "Delete User"}
                          disabled={processingId === user.userId || user.userId === currentUserId}
                        >
                          <FaTrashAlt color={user.userId === currentUserId ? "#f8717180" : "#ef4444"} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default UserManagementPage;
